import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "./Header";
import Field from "../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../Components/Icon-SVG/TrashBucketIcon";

import { addNotification } from "../../../store/actions/notifierActions";
import {
  RequestGetBlacklistSettings,
  RequestSetBlacklistSettings,
} from "../../../store/actions/mailGatewayActions";

const BlacklistForm = ({ selectedProduct, selectedHost }) => {
  const dispatch = useDispatch();

  const blacklistFromDB = useSelector((state) => state.mailGateway.blacklistSettings);
  const loading = useSelector((state) => state.mailGateway.whitelistSettingsLoading);

  const [blacklistUI, setBlacklistUI] = useState([]);

  const [newSenderEmail, setNewSenderEmail] = useState({ email: "", description: "" });
  const [newSenderDomain, setNewSenderDomain] = useState({ domain: "", description: "" });
  const [newDomain, setNewDomain] = useState({ domain: "", description: "" });
  const [newHash, setNewHash] = useState({ hash: "", description: "" });

  useEffect(() => {
    if (selectedHost) {
      dispatch(RequestGetBlacklistSettings({ hostname: selectedHost }));
    }
  }, [selectedHost, dispatch]);

  useEffect(() => {
    if (blacklistFromDB && selectedHost) {
      setBlacklistUI(blacklistFromDB);
    }
  }, [blacklistFromDB, selectedHost]);

  const handleSave = () => {
    if (selectedHost) {
      dispatch(RequestSetBlacklistSettings({ hostname: selectedHost, settings: blacklistUI }));
    }
  };

  const handleAddSenderEmail = () => {
    const emailBlacklist = blacklistUI.filter((el) => el.type === "mail");
    const trimmedEmail = newSenderEmail.email.trim();
    if (emailBlacklist.some((wl) => wl.value === trimmedEmail)) {
      dispatch(
        addNotification({
          message: "Email already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else if (
      trimmedEmail !== "" &&
      trimmedEmail.includes("@") &&
      trimmedEmail.split("@")[1] !== ""
    ) {
      setBlacklistUI((prevBlacklist) => [
        ...prevBlacklist,
        {
          type: "mail",
          value: trimmedEmail,
          description: newSenderEmail.description.trim(),
        },
      ]);
      setNewSenderEmail({ email: "", description: "" });
    } else {
      dispatch(
        addNotification({
          message: "Enter a valid email address (user@domain.tld)",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    }
  };

  const handleAddSenderDomain = () => {
    const domainBlacklist = blacklistUI.filter((el) => el.type === "domain");
    const trimmedDomain = newSenderDomain.domain.trim();
    if (domainBlacklist.some((wl) => wl.value === trimmedDomain)) {
      dispatch(
        addNotification({
          message: "Domain already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else if (
      trimmedDomain !== "" &&
      trimmedDomain.includes(".") &&
      trimmedDomain.split(".")[0] !== "" &&
      trimmedDomain.split(".")[1] !== ""
    ) {
      setBlacklistUI((prevBlacklist) => [
        ...prevBlacklist,
        {
          type: "domain",
          value: trimmedDomain,
          description: newSenderDomain.description.trim(),
        },
      ]);
      setNewSenderDomain({ domain: "", description: "" });
    } else {
      dispatch(
        addNotification({
          message: "Enter a valid domain (domain.tld)",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    }
  };

  const handleAddDomain = () => {
    const domainWhitelist = blacklistUI.filter((el) => el.type === "ddomain");
    const trimmedDomain = newDomain.domain.trim();
    if (domainWhitelist.some((wl) => wl.value === trimmedDomain)) {
      dispatch(
        addNotification({
          message: "Domain already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else if (
      trimmedDomain !== "" &&
      trimmedDomain.includes(".") &&
      trimmedDomain.split(".")[0] !== "" &&
      trimmedDomain.split(".")[1] !== ""
    ) {
      setBlacklistUI((prevWhitelist) => [
        ...prevWhitelist,
        {
          type: "ddomain",
          value: trimmedDomain,
          description: newDomain.description.trim(),
        },
      ]);
      setNewDomain({ domain: "", description: "" });
    } else {
      dispatch(
        addNotification({
          message: "Enter a valid domain (domain.tld)",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    }
  };

  const handleAddHash = () => {
    const hashBlacklist = blacklistUI.filter((el) => el.type === "hash");
    const trimmedHash = newHash.hash.trim().toLowerCase();
    const isHex = /^[a-f0-9]+$/.test(trimmedHash);
    const validLengths = [32, 64];

    if (hashBlacklist.some((wl) => wl.value === trimmedHash)) {
      dispatch(
        addNotification({
          message: "Hash already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else if (isHex && validLengths.includes(trimmedHash.length)) {
      setBlacklistUI((prevBlacklist) => [
        ...prevBlacklist,
        {
          type: "hash",
          value: trimmedHash,
          description: newHash.description.trim(),
        },
      ]);
      setNewHash({ hash: "", description: "" });
    } else {
      dispatch(
        addNotification({
          message: "Enter a valid hash (md5 or sha256)",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    }
  };

  const handleRemove = (index, type) => {
    setBlacklistUI((prevState) => {
      const blacklistOfType = prevState.filter((el) => el.type === type);

      return prevState.filter((el) => {
        if (el.type !== type) return true;
        else {
          return blacklistOfType[index].value !== el.value;
        }
      });
    });
  };

  return (
    <>
      <Header selectedProduct={selectedProduct} selectedHost={selectedHost} />
      <form className="products-body-form" onSubmit={(e) => e.preventDefault()}>
        <div className="products-body-form__group-line">
          <Field
            label="Sender Email"
            width="300px"
            value={newSenderEmail.email}
            onChange={(e) =>
              setNewSenderEmail((prevState) => ({ ...prevState, email: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter email address (e.g., user@domain.tld)"
            name="newSenderEmail"
            type="email"
          />
          <Field
            label="Description"
            width="300px"
            value={newSenderEmail.description}
            onChange={(e) =>
              setNewSenderEmail((prevState) => ({ ...prevState, description: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter description"
            name="newSenderEmailDesc"
            type="text"
          />
          <button
            type="button"
            className="products-body-form__button"
            onClick={handleAddSenderEmail}>
            +
          </button>
        </div>
        <ul className="sender-emails-list" style={{ gridColumn: "span 2" }}>
          {blacklistUI
            ?.filter((item) => item.type === "mail")
            ?.length > 0 ? (
            blacklistUI
              ?.filter((item) => item.type === "mail")
              ?.map((item, index) => (
                <li key={index} className="sender-email-item">
                  {item.value} | {item.description}
                  <button
                    type="button"
                    style={{ display: "flex" }}
                    onClick={() => handleRemove(index, "mail")}
                  >
                    <TrashBucketIcon width="16" height="16" />
                    <p style={{ marginLeft: "5px" }}>Delete</p>
                  </button>
                </li>
              ))
          ) : (
            <li className="no-data">&nbsp;No Data</li>
          )}
        </ul>

        <div className="products-body-form__group-line products-body-form__group-line--space">
          <Field
            label="Sender Domain"
            width="300px"
            value={newSenderDomain.domain}
            onChange={(e) =>
              setNewSenderDomain((prevState) => ({ ...prevState, domain: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter domain (e.g., domain.tld)"
            name="newDomain"
            type="text"
          />
          <Field
            label="Description"
            width="300px"
            value={newSenderDomain.description}
            onChange={(e) =>
              setNewSenderDomain((prevState) => ({ ...prevState, description: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter description"
            name="newDomainDesc"
            type="text"
          />
          <button
            type="button"
            className="products-body-form__button"
            onClick={handleAddSenderDomain}>
            +
          </button>
        </div>
        <ul className="sender-emails-list" style={{ gridColumn: "span 2" }}>
          {blacklistUI
            ?.filter((item) => item.type === "domain")
            ?.length > 0 ? (
            blacklistUI
              ?.filter((item) => item.type === "domain")
              ?.map((item, index) => (
                <li key={index} className="sender-email-item">
                  {item.value} | {item.description}
                  <button
                    type="button"
                    style={{ display: "flex" }}
                    onClick={() => handleRemove(index, "domain")}
                  >
                    <TrashBucketIcon width="16" height="16" />
                    <p style={{ marginLeft: "5px" }}>Delete</p>
                  </button>
                </li>
              ))
          ) : (
            <li className="no-data">&nbsp;No Data</li>
          )}
        </ul>

        <div className="products-body-form__group-line products-body-form__group-line--space">
          <Field
            label="Hash"
            width="300px"
            value={newHash.hash}
            onChange={(e) => setNewHash((prevState) => ({ ...prevState, hash: e.target.value }))}
            parentClassName="p-r-20"
            placeholder="Enter hash (md5 or sha256)"
            name="newDomain"
            type="text"
          />
          <Field
            label="Description"
            width="300px"
            value={newHash.description}
            onChange={(e) =>
              setNewHash((prevState) => ({ ...prevState, description: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter description"
            name="newHashDesc"
            type="text"
          />
          <button type="button" className="products-body-form__button" onClick={handleAddHash}>
            +
          </button>
        </div>
        <ul className="sender-emails-list" style={{ gridColumn: "span 2" }}>
          {blacklistUI
            ?.filter((item) => item.type === "hash")
            ?.length > 0 ? (
            blacklistUI
              ?.filter((item) => item.type === "hash")
              ?.map((item, index) => (
                <li key={index} className="sender-email-item">
                  {item.value} | {item.description}
                  <button
                    type="button"
                    style={{ display: "flex" }}
                    onClick={() => handleRemove(index, "hash")}
                  >
                    <TrashBucketIcon width="16" height="16" />
                    <p style={{ marginLeft: "5px" }}>Delete</p>
                  </button>
                </li>
              ))
          ) : (
            <li className="no-data">&nbsp;No Data</li>
          )}
        </ul>

        <div className="products-body-form__group-line products-body-form__group-line--space">
          <Field
            label="Domain"
            width="300px"
            value={newDomain.domain}
            onChange={(e) => setNewDomain((prevState) => ({ ...prevState, domain: e.target.value }))}
            parentClassName="p-r-20"
            placeholder="Enter domain (e.g., domain.tld)"
            name="newDomain"
            type="text"
          />
          <Field
            label="Description"
            width="300px"
            value={newDomain.description}
            onChange={(e) =>
              setNewDomain((prevState) => ({ ...prevState, description: e.target.value }))
            }
            parentClassName="p-r-20"
            placeholder="Enter description"
            name="newDomainDesc"
            type="text"
          />
          <button type="button" className="products-body-form__button" onClick={handleAddDomain}>
            +
          </button>
        </div>
        <ul className="sender-emails-list" style={{ gridColumn: "span 2" }}>
          {blacklistUI
            ?.filter((item) => item.type === "ddomain")
            ?.length > 0 ? (
            blacklistUI
              ?.filter((item) => item.type === "ddomain")
              ?.map((item, index) => (
                <li key={index} className="sender-email-item">
                  {item.value} | {item.description}
                  <button
                    type="button"
                    style={{ display: "flex" }}
                    onClick={() => handleRemove(index, "ddomain")}
                  >
                    <TrashBucketIcon width="16" height="16" />
                    <p style={{ marginLeft: "5px" }}>Delete</p>
                  </button>
                </li>
              ))
          ) : (
            <li className="no-data">&nbsp;No Data</li>
          )}
        </ul>
        <br />
        <button
          type="button"
          className="save-button primary-button"
          disabled={loading}
          onClick={handleSave}>
          Save Settings
        </button>
      </form>
    </>
  );
};

export default BlacklistForm;
